<template>
  <div class="login-page">
    <!-- Background com gradiente animado -->
    <div class="background-animation">
      <div class="gradient-bg"></div>
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
      </div>
    </div>

    <!-- Container principal -->
    <div class="login-container">
      <!-- Logo e título -->
      <div class="logo-section">
        <div class="logo-container">
          <img src="../assets/logo-sigel.png" alt="SisGel Logo" class="logo" />
        </div>
        <h1 class="welcome-title">{{ $t('login.welcome') }}</h1>
        <p class="welcome-subtitle">{{ $t('login.subtitle') }}</p>
      </div>

      <!-- Formulário de login -->
      <div class="login-form-container">
        <div class="form-header">
          <h2 class="form-title">
            <i class="fas fa-user-circle"></i>
            {{ $t('login.formTitle') }}
          </h2>
          <p class="form-subtitle">{{ $t('login.formSubtitle') }}</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <!-- Campo de usuário -->
          <div class="form-group">
            <div class="input-container">
              <i class="fas fa-user input-icon"></i>
              <input
                type="text"
                v-model="username"
                id="username"
                placeholder=""
                required
                class="form-input"
                :class="{ 'has-error': showUsernameError }"
              />
              <label for="username" class="input-label">{{ $t('login.username') }}</label>
            </div>
            <span v-if="showUsernameError" class="error-text">
              <i class="fas fa-exclamation-circle"></i>
              {{ usernameError }}
            </span>
          </div>

          <!-- Campo de senha -->
          <div class="form-group">
            <div class="input-container">
              <i class="fas fa-lock input-icon"></i>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                id="password"
                placeholder=""
                required
                class="form-input"
                :class="{ 'has-error': showPasswordError }"
              />
              <label for="password" class="input-label">{{ $t('login.password') }}</label>
              <button
                type="button"
                @click="togglePassword"
                class="password-toggle"
                :class="{ 'active': showPassword }"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <span v-if="showPasswordError" class="error-text">
              <i class="fas fa-exclamation-circle"></i>
              {{ passwordError }}
            </span>
          </div>

          <!-- Botão de login -->
          <button 
            type="submit" 
            class="login-btn"
            :disabled="authStore.isLoading"
            :class="{ 'loading': authStore.isLoading }"
          >
            <span v-if="!authStore.isLoading" class="btn-content">
              <i class="fas fa-sign-in-alt"></i>
              {{ $t('login.enter') }}
            </span>
            <span v-else class="loading-content">
              <i class="fas fa-spinner fa-spin"></i>
              {{ $t('login.entering') }}
            </span>
          </button>

          <!-- Mensagem de erro -->
          <div v-if="authStore.error" class="error-message">
            <i class="fas fa-exclamation-triangle"></i>
            <span>{{ authStore.error }}</span>
          </div>
        </form>

        <!-- Informações adicionais -->
        <div class="form-footer">
          <p class="help-text">
            <i class="fas fa-info-circle"></i>
            {{ $t('login.help') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="page-footer">
      <p>&copy; {{ currentYear }} {{ $t('login.footer') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/admin/authStore";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { useLgpdConsent } from "@/composables/useLgpdConsent";

const { t } = useI18n();

const router = useRouter();
const authStore = useAuthStore();

const currentYear = new Date().getFullYear();
const username = ref("");
const password = ref("");
const showPassword = ref(false);

const showUsernameError = ref(false);
const showPasswordError = ref(false);
const usernameError = ref("");
const passwordError = ref("");

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const validateForm = (): boolean => {
  let isValid = true;
  
  showUsernameError.value = false;
  showPasswordError.value = false;
  usernameError.value = "";
  passwordError.value = "";
  if (!username.value.trim()) {
    showUsernameError.value = true;
    usernameError.value = t('login.usernameRequired');
    isValid = false;
  } else if (username.value.length < 3) {
    showUsernameError.value = true;
    usernameError.value = t('login.usernameMinLength');
    isValid = false;
  }

  if (!password.value.trim()) {
    showPasswordError.value = true;
    passwordError.value = t('login.passwordRequired');
    isValid = false;
  } else if (password.value.length < 4) {
    showPasswordError.value = true;
    passwordError.value = t('login.passwordMinLength');
    isValid = false;
  }

  return isValid;
};

const handleLogin = async () => {
  if (!validateForm()) {
    return;
  }

  try {
    const credentials = {
      username: username.value.trim(),
      password: password.value,
    };

    await authStore.login(credentials);

    ElNotification({
      title: t('common.success'),
      message: t('login.loginSuccess'),
      type: 'success',
    });

    // Check LGPD consent before proceeding
    const consentGranted = await useLgpdConsent()
    if (!consentGranted) return

    if (!authStore.lojaId) {
      router.push("/setup");
    } else {
      router.push("/home");
    }

  } catch (error: any) {
    ElNotification({
      title: t('login.loginError'),
      message: authStore.error || t('login.loginError'),
      type: 'error',
    });
  }
};
</script>
